import React, { useState } from 'react';
import { X, Clock, Ban, CheckCircle, XCircle, Building2, User, Calendar } from 'lucide-react';
import { useQuery } from 'react-query';
import { api } from '../utils/api';

const VendorHistoryModal = ({ isOpen, onClose, vendorId, vendorName }) => {
  const [selectedItem, setSelectedItem] = useState(null);

  const { data: blockHistory, isLoading } = useQuery(
    ['vendor-block-history', vendorId],
    () => api.get(`/vendors/${vendorId}/block-history`),
    {
      enabled: isOpen && !!vendorId,
      select: (data) => data?.data?.data || data?.data
    }
  );

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const getActionIcon = (action) => {
    return action === 'blocked' ? <Ban className="h-4 w-4 text-red-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />;
  };

  const getBlockTypeBadge = (blockType) => {
    return (
      <span className={`badge ${blockType === 'permanent' ? 'badge-error' : 'badge-warning'}`}>
        {blockType === 'permanent' ? 'Permanent' : 'Temporary'}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {vendorName} - Block History
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Block History</h3>
          
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading block history...</p>
            </div>
          ) : blockHistory?.blockHistory && blockHistory.blockHistory.length > 0 ? (
            <div className="space-y-3">
              {blockHistory.blockHistory.map((entry) => (
                <div
                  key={entry._id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedItem(selectedItem?._id === entry._id ? null : entry)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getActionIcon(entry.action)}
                      <div>
                        <div className="font-medium text-gray-900">
                          {entry.action === 'blocked' ? 'Blocked' : 'Unblocked'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatTime(entry.action === 'blocked' ? entry.blockedAt : entry.unblockedAt)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {entry.blockType && getBlockTypeBadge(entry.blockType)}
                      <span className="text-sm text-gray-500">
                        by {entry.adminName}
                      </span>
                    </div>
                  </div>

                  {selectedItem?._id === entry._id && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                      {entry.reason && (
                        <div>
                          <span className="font-medium text-gray-700">Reason:</span>
                          <span className="ml-2 text-gray-600">{entry.reason}</span>
                        </div>
                      )}
                      
                      {entry.blockExpiry && (
                        <div>
                          <span className="font-medium text-gray-700">Expires:</span>
                          <span className="ml-2 text-gray-600">{formatTime(entry.blockExpiry)}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <User className="h-4 w-4 mr-1" />
                          {entry.adminName}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatTime(entry.action === 'blocked' ? entry.blockedAt : entry.unblockedAt)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <Ban className="empty-state-icon" />
              <h3 className="empty-state-title">No Block History</h3>
              <p className="empty-state-description">
                This vendor has no block history.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorHistoryModal; 